import { pushItem, removeItem, replaceItem } from './reducerUtils';
import { createCartOrderItem } from './DishUtils';
import { CurrencyUtils } from '@wix/wixrest-utils';
import _ from 'lodash';

export function createPayPerDishSplit({ payments }) {
  return payments.map(() => []);
}

function getIndexOfAssociatedPayment({ ppd, orderItemIndex }) {
  for (let i = 0; i < ppd.length; i++) {
    if (ppd[i].indexOf(orderItemIndex) !== -1) {
      return i;
    }
  }

  return -1;
}

function disassociateDishFromPayment({ ppd, orderItemIndex }) {
  const indexOfAssociatedPayment = getIndexOfAssociatedPayment({ ppd, orderItemIndex });
  if (indexOfAssociatedPayment !== -1) {
    const dishes = removeItem(ppd[indexOfAssociatedPayment], ppd[indexOfAssociatedPayment].indexOf(orderItemIndex));
    return replaceItem(ppd, indexOfAssociatedPayment, dishes);
  }

  return ppd;
}

export function toggleDishToPaymentAssociation({ ppd, paymentIndex, orderItemIndex }) {
  const indexOfAssociatedPayment = getIndexOfAssociatedPayment({ ppd, orderItemIndex });
  const disassociated = disassociateDishFromPayment({ ppd, orderItemIndex });
  if (indexOfAssociatedPayment === -1) {
    const dishes = pushItem(ppd[paymentIndex], orderItemIndex);
    return replaceItem(disassociated, paymentIndex, dishes);
  } else {
    return disassociated;
  }
}

function getDisplayablePaymentMethod(paymentMethod) {
  if (!paymentMethod || !paymentMethod.cardToken) {
    return null;
  }

  return {
    lastDigits: paymentMethod.cardToken.creditCard.lastDigits,
    network: paymentMethod.cardToken.creditCard.network,
    holderName: paymentMethod.cardToken.creditCard.additionalFields.holderName,
  };
}

export function getDisplayableSpreadDishes({
  orderItems,
  payments,
  itemsHash,
  currency,
  i18n,
  locale,
  ppd,
  paymentIndex,
}) {
  const flatOrderItems = flattenOrderItems({ orderItems });
  return flatOrderItems.map((orderItem, orderItemIndex) => {
    const { fullOrderItem, choicesStr } = createCartOrderItem({ orderItem, itemsHash, currency, i18n, locale });
    const associatedPaymentIndex = getIndexOfAssociatedPayment({ ppd, orderItemIndex });

    return {
      title: fullOrderItem.title[locale],
      price: CurrencyUtils.toStr(fullOrderItem.priceEach / 100, currency, locale),
      priceRaw: fullOrderItem.priceEach,
      choicesStr,
      checked: associatedPaymentIndex === paymentIndex,
      indeterminate: associatedPaymentIndex !== paymentIndex && associatedPaymentIndex !== -1,
      displayablePaymentMethod: getDisplayablePaymentMethod(payments[associatedPaymentIndex]),
      paymentType: _.get(payments[associatedPaymentIndex], 'type', null),
    };
  });
}

export function getDisplayableSpreadDishesByPaymentIndex({
  orderItems,
  payments,
  itemsHash,
  currency,
  i18n,
  locale,
  ppd,
  paymentIndex,
}) {
  const displayableSpreadDishes = getDisplayableSpreadDishes({
    orderItems,
    payments,
    itemsHash,
    currency,
    i18n,
    locale,
    ppd,
    paymentIndex,
  });

  return _(displayableSpreadDishes)
    .filter(dish => dish.checked)
    .map(dish => _.pick(dish, ['title', 'price', 'choicesStr']))
    .value();
}

export const alignIndexActions = {
  ADD: 'add',
  REMOVE: 'remove',
};

export function alignIndex({ ppd, action, orderItemIndex }) {
  if (action === alignIndexActions.ADD) {
    return ppd.map(dishes => dishes.map(idx => (idx < orderItemIndex ? idx : idx + 1)));
  } else if (action === alignIndexActions.REMOVE) {
    const disassociated = disassociateDishFromPayment({ ppd, orderItemIndex });
    return disassociated.map(dishes => dishes.map(idx => (idx < orderItemIndex ? idx : idx - 1)));
  }
}

export function appendPaymentMethod({ ppd }) {
  return [...ppd, []];
}

export function removePaymentMethod({ ppd, index }) {
  return removeItem(ppd, index);
}

export function convertCartIndexToFlatIndex({ cartOrderItems, index }) {
  let result = 0;

  for (let i = 0; i <= index; i++) {
    result += cartOrderItems[i].count || 1;
  }

  return result - 1;
}

function getPrices({ orderItems, itemsHash, locale, currency, i18n }) {
  const flatOrderItems = flattenOrderItems({ orderItems });

  return flatOrderItems.map(orderItem => {
    const { fullOrderItem } = createCartOrderItem({ orderItem, itemsHash, currency, i18n, locale });
    return fullOrderItem.priceEach || 0;
  });
}

export function getTotalAmountsPerPayment({ orderItems, itemsHash, locale, currency, i18n, ppd }) {
  const prices = getPrices({ orderItems, itemsHash, locale, currency, i18n });
  return ppd.map(dishes => {
    let result = 0;
    dishes.forEach(dish => {
      result += prices[dish];
    });
    return result;
  });
}

export function flattenOrderItems({ orderItems }) {
  const result = [];

  orderItems.forEach(orderItem => {
    _.times(orderItem.count || 1, () => {
      result.push({ ...orderItem, count: 1 });
    });
  });

  return result;
}
